import { useState } from 'react';

import { isInAppBrowser, isIOS, isStandalone } from '../lib/browserEnv';

/**
 * iOS 홈 화면 추가 코치마크 — 하단 고정 배너.
 *
 * 아이폰 브라우저로 접속했고 아직 설치하지 않은 사람에게만 뜬다.
 * 인앱 브라우저에서는 안 뜬다(거기선 홈 화면 추가가 안 되고, 로그인 화면의
 * "기본 브라우저로 열기" 안내가 먼저다 — 나와서 접속하면 이 배너가 이어받는다).
 *
 * iOS 16.4+부터는 크롬 등 서드파티 브라우저에서도 홈 화면 추가가 되므로
 * Safari로 강제 이동시키지 않고(공개 API도 없다) 지금 브라우저에서 안내한다.
 * 안드로이드는 beforeinstallprompt 기반 설치 UI가 따로 있어 대상에서 뺀다.
 */

const DISMISS_KEY = 'tomato_a2hs_dismissed_at';
/** 닫아도 이 기간이 지나면 한 번 더 슬쩍 보여준다. */
const RESHOW_AFTER_DAYS = 14;

function dismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number.parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < RESHOW_AFTER_DAYS * 86_400_000;
  } catch {
    return true; // storage가 막힌 환경(프라이빗 모드 등)에선 조용히 안 띄운다.
  }
}

/** iOS 공유 아이콘(네모에서 화살표가 올라오는 그 모양) — 문구만으론 못 찾는 사람이 많다. */
function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" aria-hidden>
      <path
        d="M12 3v11M8.5 6.5 12 3l3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 10H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * 지금 이 배너가 뜰 자리인가. 하단 배너를 두 개 쌓지 않으려고 패스키 넛지가 이걸 보고 양보한다.
 * (홈 화면 추가가 먼저다 — 앱으로 열려야 재방문이 붙고, 그다음이 로그인 편의다.)
 */
export function addToHomeEligible(): boolean {
  return isIOS() && !isStandalone() && !isInAppBrowser() && !dismissedRecently();
}

export function AddToHomeBanner() {
  const [visible, setVisible] = useState(addToHomeEligible);

  if (!visible) return null;

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* 저장 실패면 다음 방문에 또 뜰 뿐 — 치명적이지 않다. */
    }
  }

  return (
    // AppShell의 하단 고정 스택 안에서 렌더된다 — 모바일에선 탭바가, 그 외(iPad 등)에선
    // 자신이 최하단이라 safe-area 패딩을 스스로 챙긴다.
    <div className="pointer-events-auto px-4 pb-3 sm:pb-[max(env(safe-area-inset-bottom),12px)]">
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-2xl bg-ink-900/95 px-4 py-3 text-ink-50 shadow-xl backdrop-blur">
        <span className="text-[22px]">📱</span>
        <p className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="inline-flex translate-y-[3px] items-center text-tomato-300">
            <ShareIcon />
          </span>{' '}
          <b>공유 버튼</b>을 누르고 <b>홈 화면에 추가</b>하면 앱처럼 한 번에 열려요
        </p>
        <button
          type="button"
          aria-label="닫기"
          onClick={dismiss}
          className="shrink-0 rounded-full px-2 py-1 text-[13px] text-ink-50/60 transition hover:text-ink-50"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
